/**
 * The laws explorer's URLs (MAR-948 split of `pages/app/laws/index.vue`).
 *
 * A drill's type/year/month live in the PATH — `/app/laws/browse/decree/1942/6`, served by the
 * `app-laws-browse` route `nuxt.config.ts` registers for the same page — and every other filter
 * stays in the query. A norm reached from a drill keeps that context in its own URL
 * (`/app/laws/browse/decree/1942/:id`, the `app-laws-browse-detail` route) so back, forward and reload
 * return to the same list. These were three hand-built copies of the same segment walk.
 */

export const LAWS_ROOT = '/app/laws'

export interface DrillPosition {
  type: string | null
  year: number | string | null
  month: number | string | null
}

/** `[type, year, month]`, each only if the one before it is set. */
function drillSegments({ type, year, month }: DrillPosition): string[] {
  if (!type) return []
  const segments = [type]
  if (year) {
    segments.push(String(year))
    if (month) segments.push(String(month))
  }
  return segments
}

/** The path of a drill position: the overview when nothing is drilled. */
export function browsePath(position: DrillPosition): string {
  const segments = drillSegments(position)
  return segments.length ? `${LAWS_ROOT}/browse/${segments.join('/')}` : LAWS_ROOT
}

/**
 * Where a norm listed in a drill links to. Only a drilled nacional list has a `:type` to anchor
 * to; anything else (a municipality) uses the plain resolver route, which redirects to the
 * canonical browse URL itself (see `laws/id/[id].vue`).
 */
export function drillNormPath(position: DrillPosition, normId: string): string {
  const segments = drillSegments(position)
  return segments.length ? `${LAWS_ROOT}/browse/${[...segments, normId].join('/')}` : `${LAWS_ROOT}/id/${normId}`
}

/**
 * Back-compat for the query-based drill URLs that predate the path form:
 * `/app/laws?type=decree/1942` and `/app/laws?type=decree&year=1942[&month=6]` →
 * `/app/laws/browse/decree/1942[/6]`, keeping every other query parameter. `null` for anything else.
 */
export function legacyBrowseLocation(to: { path: string, query: Record<string, unknown> }):
  { path: string, query: Record<string, string> } | null {
  const rawType = to.query.type as string | undefined
  if (to.path !== LAWS_ROOT || !rawType) return null
  const [type, pathYear, pathMonth] = rawType.split('/')
  const query: Record<string, string> = {}
  for (const [key, val] of Object.entries(to.query)) {
    if (key !== 'type' && key !== 'year' && key !== 'month') query[key] = val as string
  }
  return {
    path: browsePath({
      type: type!,
      year: pathYear ?? (to.query.year as string | undefined) ?? null,
      month: pathMonth ?? (to.query.month as string | undefined) ?? null,
    }),
    query,
  }
}
